// The timeseries dataset provider — the unified SSOT for both top-level
// `(datapoints …)` queries and a view's per-row timeseries cells.
//
// A timeseries dataset is ROW-PER-SERIES: one row per selected series,
// carrying the series identity (`externalId`, optional `space`) plus a
// nested `datapoints` cell. The cell is a 3-column inner dataset
// `{ timestamp, value, isString }` — NO `label` column (and no series-unit
// string column: per-row unit truth rides on the boxed `value` cells);
// categorical-ness collapses to "the `value` column is string-typed".
//
// `buildSource({ ref, valueType? })` is the single constructor for one
// series' nested cell source. Both the top-level `(datapoints)` op (which
// assembles a row per selected ref) and a view provider minting a per-row
// timeseries cell call it, so the two paths can never drift in inner
// shape — they share this one minting function.
//
// The inner `value` column type:
//   - CONCRETE (`numberCol` / `stringCol`) when a view passes `valueType`
//     — the view's categorical spec knows whether the series is numeric
//     or string.
//   - `unknownCol` when omitted (a top-level `(datapoints)` selection may
//     mix numeric and string series, and a nested-dataset column has ONE
//     inner schema). The fetched concrete type refines `unknown` per
//     series at materialisation (`schemaRefines` permits unknown→concrete,
//     recursing into the inner schema).
//
// Batching is provider-internal: the production provider closes over a
// CogniteClient + QueryClient + a module-level batch scope (landing when
// the chat host wires it). For engine-level tests the datapoints fetch is
// injected as a stub at registration, so the provider is exercised without
// a CDF client. The default time window is resolved ONCE at registration
// and stored on `native`, never read from the wall clock at fetch time —
// so a source's cache key is stable across conversation turns.

import { type MintSeriesSource, SET_REF_MAX } from '#core/lib/cognite/view.ts';

import type { SourcePos } from '../ast.ts';
import { SetRefTooLargeError, UnboundedDatapointsError } from '../errors.ts';
import type { AstInput } from '../normalize.ts';
import type { Period } from '../ops/transform/date-utils.ts';
import { INTERVAL_TO_PERIOD } from '../ops/transform/time-bucket.ts';
import type { PlanNode, TsRefBucketHint } from '../plan.ts';
import { inferColumnTypeFromCells } from '../schema/refine.ts';
import {
  type ColumnType,
  dateCol,
  nestedDatasetCol,
  nullableCol,
  numberCol,
  type Schema,
  schemaFromEntries,
  stringCol,
  timestampCol,
  tsRefInnerSchema,
  unitOfType,
  unknownCol,
  withUnit,
} from '../schema/types.ts';
import {
  boxNumericCell,
  type Cell,
  type Columns,
  LazyDataset,
  MaterializedDataset,
  numericScalarOf,
} from '../value.ts';
import { TIMESERIES_GROUP } from './groups.ts';
import type { DatasetProvider, DecodedParams, DecodedPredicate, ProviderSource, SourceFetcherCtx } from './types.ts';

/**
 * The default provider `id` the timeseries provider registers under — the
 * dispatch + cache discriminator carried on `ProviderSource.providerId`.
 * Exported as the single source of truth so a consumer that mints a
 * timeseries-series source (e.g. a test fixture) tests against this
 * constant rather than a re-typed string literal.
 */
export const TIMESERIES_PROVIDER_ID = 'timeseries';

/**
 * One timeseries series identity. Mirrors CDF's `{ space?, externalId }`
 * encoding: `space` disambiguates two series that share an `externalId`
 * across spaces; absent ⇒ externalId-only resolution (CDF's bare-string
 * encoding). JSON-stable so it keys the source cache cleanly.
 */
export type TimeseriesRef = {
  readonly space?: string;
  readonly externalId: string;
};

/**
 * The absorbed-parameters blob on `ProviderSource.native` for ONE series
 * — the per-cell shape both a view's per-row cell and a top-level row's
 * `datapoints` cell mint. `ref` selects the series; `valueKind` records
 * the declared inner `value` kind so `fetch` rebuilds the same inner
 * schema the plan declared; `window` is the resolved default fetch window
 * (fixed at registration, not the wall clock); `bucket` is the
 * lazily-absorbed downstream `time-bucket` push-down hint. JSON-stable so
 * distinct selections + push-downs cache distinctly.
 */
export type TimeseriesSeriesNative = {
  readonly kind: 'series';
  readonly ref: TimeseriesRef;
  /** The inner `value` column kind, recorded so `fetch` rebuilds the declared inner schema. */
  readonly valueKind: ColumnType['kind'];
  /**
   * The declared value column's unit as JSON-stable `Unit` map entries — `valueKind`
   * alone drops it, so `fetch` mints each fetched numeric datapoint as a boxed
   * unit-bearing cell (the inner schema's column unit then derives from the cells).
   * Absent ⇒ unitless. A distinct unit re-keys the source cache distinctly, which
   * is correct: it is a distinct value typing.
   */
  readonly valueUnit?: readonly (readonly [string, number])[];
  /** The default fetch window `[startISO, endISO]`, resolved once at registration. */
  readonly window: readonly [string, string];
  /** Lazily-absorbed downstream `time-bucket` hint (window / period / aggs). */
  readonly bucket?: TsRefBucketHint;
};

/**
 * The native blob for a TOP-LEVEL `(datapoints)` query — the
 * row-per-series shape. Carries the bounded series selection; `fetch`
 * assembles one row per ref, each with a nested `datapoints` cell that is
 * itself a lazy single-series source. JSON-stable.
 */
export type TimeseriesRowsNative = {
  readonly kind: 'rows';
  readonly refs: readonly TimeseriesRef[];
};

/**
 * The provider's `ProviderSource.native` blob — a single series cell or a
 * top-level row-per-series selection. The discriminator lets one `fetch`
 * serve both the per-cell path and the top-level `(datapoints)` op.
 */
export type TimeseriesNative = TimeseriesSeriesNative | TimeseriesRowsNative;

/**
 * The params `buildSource` / `schema` consume: one series ref + the
 * optional concrete inner `value` type. Omitting `valueType` yields an
 * `unknownCol` value axis (the heterogeneous top-level default).
 */
export type TimeseriesParams = {
  readonly ref: TimeseriesRef;
  readonly valueType?: ColumnType;
};

/**
 * One series' raw datapoints, column-major, matching the inner schema's
 * column set. The injected fetch (stub in tests, the batched CDF reader
 * in production) returns this; `fetch` wraps it in a `MaterializedDataset`
 * against the inner schema. Absent ⇒ the series has no data in the window
 * (or an unknown externalId), surfaced as an empty inner dataset so a
 * downstream `(unnest …)` produces zero rows for that row.
 */
export type SeriesDatapoints = Columns;

/**
 * The datapoints-fetch seam injected at registration. Takes the resolved
 * native (series ref + window + absorbed bucket hint) and the narrow
 * fetch ctx (signal only — no eval recursion), returns the series'
 * column-major datapoints or `undefined` for no-data. Production closes a
 * CogniteClient + QueryClient + batch scope behind this; tests pass a
 * stub.
 */
export type DatapointsFetch = (
  native: TimeseriesSeriesNative,
  ctx: SourceFetcherCtx,
) => Promise<SeriesDatapoints | undefined>;

/**
 * Scan a deferred cell plan for the per-cell `time-bucket` node and
 * extract its push-down hint (window / period / aggs). The node is the
 * plain-form-on-`:timestamp` shape the per-cell lift records onto each
 * cell (a `(time-bucket cell :timestamp <interval> …)`), recognised by
 * its `:timestamp` dataset positional + an interval that maps to a known
 * `Period` — the same recognition the cell-source plan adapter uses. Returns
 * `undefined` when no such node is present (so `adaptPlan` leaves the
 * source unchanged) or when the interval is not a mappable period.
 */
export const absorbTimeBucketHint = (ops: readonly PlanNode[]): TsRefBucketHint | undefined => {
  const node = ops.find((n) => n.op === 'time-bucket');
  if (node === undefined) {
    return undefined;
  }
  const args = node.args as { readonly params?: readonly unknown[] } & Record<string, unknown>;
  const params = args.params ?? [];
  const tsCol = params[0] as { readonly $kw?: unknown } | undefined;
  const interval = params[1];
  if (tsCol?.$kw !== 'timestamp' || typeof interval !== 'string') {
    return undefined;
  }
  const period = INTERVAL_TO_PERIOD[interval];
  if (period === undefined) {
    return undefined;
  }
  return readBucketHint(args, period);
};

/**
 * Read a `TsRefBucketHint` off a per-cell `time-bucket` plan node's args,
 * given the `period` its interval decoded to. The node shape is
 * `(time-bucket :timestamp <interval> :window […] :agg {:out (sym :value)})`
 * (params[0] is the dataset, stripped on the plan node). `period` always
 * rides the hint; window comes verbatim from `:window`; aggs from the
 * synthesised `:agg` map's reducer symbols.
 */
const readBucketHint = (args: Record<string, unknown>, period: Period): TsRefBucketHint => {
  const window = readWindow(args.window);
  const aggs = readAggSymbols(args.agg);
  return { period, ...(window === undefined ? {} : { window }), ...(aggs === undefined ? {} : { aggs }) };
};

/** Verbatim `[startISO, endISO]` from a `:window` arg, or `undefined`. */
const readWindow = (window: unknown): readonly [string, string] | undefined => {
  if (Array.isArray(window) && window.length === 2 && typeof window[0] === 'string' && typeof window[1] === 'string') {
    return [window[0], window[1]];
  }
  return undefined;
};

/**
 * The reducer agg symbols from a per-cell `:agg` map `{:out (sym :value)}`.
 * Each value is an op-call whose head names the aggregate; collect them so
 * the fetcher can request matching server-side downsampling when exact.
 */
const readAggSymbols = (agg: unknown): readonly string[] | undefined => {
  const map = (agg as { readonly $map?: readonly { readonly v?: unknown }[] } | undefined)?.$map;
  if (map === undefined) {
    return undefined;
  }
  const syms = map.flatMap(({ v }) => {
    const name = (v as { readonly name?: string } | undefined)?.name;
    return name === undefined ? [] : [name];
  });
  return syms.length === 0 ? undefined : syms;
};

/**
 * Extract the bounded series selection from a decoded `datapoints`
 * predicate: every `externalId` / `space` `=` / `in` leaf, ANDed
 * together. Returns the list of selected refs.
 *
 * The guard: a `(datapoints)` with NO identity selection would stream
 * every series in the project, so an unbounded read is rejected
 * (`UnboundedDatapointsError`). A selection larger than `SET_REF_MAX`
 * exceeds the safe per-call bound and is rejected (`SetRefTooLargeError`)
 * — the same cap cross-dataset set-refs honour.
 */
export const selectSeriesRefs = (params: DecodedParams, pos: SourcePos): readonly TimeseriesRef[] => {
  const ids = collectIdentitySelection(params.filter);
  if (ids === undefined || ids.externalIds.length === 0) {
    throw new UnboundedDatapointsError(pos);
  }
  if (ids.externalIds.length > SET_REF_MAX) {
    throw new SetRefTooLargeError(ids.externalIds.length, SET_REF_MAX, pos);
  }
  // A single `space` selection applies to every externalId; absent ⇒
  // externalId-only refs (CDF's bare-string resolution).
  const space = ids.space;
  return ids.externalIds.map((externalId) => (space === undefined ? { externalId } : { externalId, space }));
};

/** The identity columns a `datapoints` selection may bound on. */
type IdentitySelection = { readonly externalIds: readonly string[]; readonly space?: string };

/**
 * Walk a decoded predicate tree collecting `externalId` / `space` `=` /
 * `in` leaves. Only an `and`-tree (or a single leaf) yields a bounded
 * selection — an `or` / `not` makes the selection non-identity-bounded,
 * so it returns `undefined` (caller rejects as unbounded). A non-identity
 * leaf is ignored (it becomes a residual client-side filter).
 */
const collectIdentitySelection = (pred: DecodedPredicate | undefined): IdentitySelection | undefined => {
  if (pred === undefined) {
    return undefined;
  }
  if ('and' in pred) {
    return pred.and.reduce<IdentitySelection | undefined>((acc, child) => {
      const childSel = collectIdentitySelection(child);
      if (childSel === undefined) {
        return acc;
      }
      return {
        externalIds: [...(acc?.externalIds ?? []), ...childSel.externalIds],
        space: childSel.space ?? acc?.space,
      };
    }, undefined);
  }
  if ('or' in pred || 'not' in pred) {
    return undefined;
  }
  return leafIdentity(pred);
};

/** One predicate leaf → its identity contribution, or `undefined`. */
const leafIdentity = (leaf: {
  readonly col: string;
  readonly op: string;
  readonly value?: AstInput;
}): IdentitySelection | undefined => {
  if (leaf.col === 'externalId') {
    const externalIds = literalStrings(leaf.op, leaf.value);
    return externalIds === undefined ? undefined : { externalIds };
  }
  if (leaf.col === 'space' && leaf.op === '=' && typeof leaf.value === 'string') {
    return { externalIds: [], space: leaf.value };
  }
  return undefined;
};

/**
 * The string values an `=` / `in` leaf selects: `=` ⇒ a single literal;
 * `in` ⇒ a literal vector. Any other op (or a non-literal RHS) is not an
 * identity selection and yields `undefined`.
 */
const literalStrings = (op: string, value: AstInput | undefined): readonly string[] | undefined => {
  if (op === '=' && typeof value === 'string') {
    return [value];
  }
  if (op === 'in' && Array.isArray(value) && value.every((v) => typeof v === 'string')) {
    return value as readonly string[];
  }
  return undefined;
};

/**
 * The row-per-series OUTER schema a top-level `(datapoints)` query
 * declares: `externalId` + nullable `space` identity columns plus a
 * nested `datapoints` cell. The cell's inner value axis is `unknown` (a
 * top-level selection may mix numeric and string series; the fetched
 * concrete type refines `unknown` per series). SSOT for the top-level
 * shape, shared by the op's declared schema and the fetched rows.
 */
export const rowsOuterSchema = (): Schema =>
  schemaFromEntries([
    ['externalId', stringCol],
    ['space', nullableCol(stringCol)],
    ['datapoints', nestedDatasetCol(tsRefInnerSchema(unknownCol))],
  ]);

/**
 * Mint the top-level row-per-series `ProviderSource` for a bounded
 * selection — the descriptor the `datapoints` op stores on its
 * `LazyDataset`. Fetch dispatches on `native.kind === 'rows'` and calls
 * `buildDatapointsRows`. Paired with `rowsOuterSchema` as the declared
 * schema.
 */
export const datapointsRowsSource = (providerId: string, refs: readonly TimeseriesRef[]): ProviderSource => ({
  kind: 'provider',
  providerId,
  native: { kind: 'rows', refs } satisfies TimeseriesRowsNative,
});

/**
 * Assemble a row-per-series `MaterializedDataset` for a top-level
 * `(datapoints)` query: one row per selected ref, with `externalId` /
 * `space` identity columns and a nested `datapoints` cell. Each cell is a
 * `LazyDataset` over the series' provider source (built via the SSOT
 * single-series `buildSource`), so the datapoints fetch is deferred until
 * the cell is materialised — identical to a view's per-row timeseries
 * cell, which is exactly the shape-identity invariant.
 */
export const buildDatapointsRows = (mint: MintSeriesSource, refs: readonly TimeseriesRef[]): MaterializedDataset => {
  // Top-level selection mixes numeric + string series, so the cell value
  // axis is `unknown` (refined per series at materialisation).
  const innerSchema = tsRefInnerSchema(unknownCol);
  const cells: Cell[] = refs.map((ref) => {
    const { source } = mint({ ref });
    return new LazyDataset({ source, ops: [] }, innerSchema) as Cell;
  });
  const columns: Columns = {
    externalId: refs.map((r) => r.externalId),
    space: refs.map((r) => r.space ?? null),
    datapoints: cells,
  };
  // Timeseries refs are not CDF DMS instances, so this dataset carries no
  // typed identity axis.
  return new MaterializedDataset(columns, rowsOuterSchema(), undefined);
};
/** Empty column-major datapoints matching the inner `{timestamp, value, isString}` columns. */
const emptyInnerColumns = (): Columns => ({ timestamp: [], value: [], isString: [] });

/**
 * Lower bound of the plausible epoch-millisecond calendar range.
 * `9.5e11` ms ≈ 2000-02-29 — comfortably below any realistic dashboard
 * timestamp while excluding small magnitudes (counts, rates, ratios) that
 * would otherwise be mistaken for instants.
 */
const EPOCH_MS_MIN = 9.5e11;

/**
 * Upper bound of the plausible epoch-millisecond calendar range.
 * `4.0e12` ms ≈ 2096-10 — high enough for any forward-looking timestamp,
 * yet far below run-identifier magnitudes (e.g. `1.78e15`) so a run id is
 * never mis-detected as a date.
 */
const EPOCH_MS_MAX = 4.0e12;

/**
 * True when `n` is a finite number inside the bounded epoch-millisecond
 * calendar range ({@link EPOCH_MS_MIN}..{@link EPOCH_MS_MAX}) — the only
 * magnitudes plausibly representing a real-world instant. Deliberately
 * narrow: it excludes both ordinary numeric magnitudes (counts, rates) and
 * oversized identifiers (run ids), so automatic epoch→date detection never
 * fires on a value that is not a date.
 */
const isEpochMsLike = (n: number): boolean => Number.isFinite(n) && n >= EPOCH_MS_MIN && n <= EPOCH_MS_MAX;

/**
 * Best-effort scalar {@link ColumnType} for a column of raw datapoint values
 * when no authoritative `isString` flag is available (a measure with no
 * datapoints in any source row leaves nothing to read the flag from).
 *
 * Resolution, value-parse only (the last-resort rung of the typing ladder):
 *
 *   - all non-null, non-empty values coerce — a unit-bearing cell via its
 *     bare number (`numericScalarOf`), else via `Number(String(v).trim())`
 *     — to a FINITE number, AND every one lands in the bounded epoch-ms
 *     range ({@link isEpochMsLike}) ⇒ `timestamp` (epoch-ms instants render
 *     as dates rather than raw integers);
 *   - all numeric but at least one outside the epoch range ⇒ `number`;
 *   - otherwise ⇒ `string`. An all-null / empty column has no evidence of
 *     numeric-ness, so it conservatively types as `string` (the safe default
 *     that never coerces a literal value to `NaN`).
 *
 * Returns a bare leaf type with no unit — the caller stamps the canonical
 * column's unit onto a numeric result, since unit resolution is domain-side.
 *
 * Distinct from `inferColumnTypeFromCells` (expr/schema/refine.ts), which
 * recovers a cell's JS-runtime type structurally (no string→number parse, no
 * epoch detection) for general unknown-column healing. This helper is the
 * timeseries-datapoint value-parse rung that also auto-detects epoch-ms
 * instants — kept separate so epoch detection never fires on a non-timeseries
 * column.
 */
const inferScalarType = (values: readonly Cell[]): ColumnType => {
  const present = values.filter((v): v is Exclude<Cell, null> => v !== null && String(v).trim() !== '');
  if (present.length === 0) {
    return stringCol;
  }
  const numbers = present.map((v: Cell) => numericScalarOf(v) ?? Number(String(v).trim()));
  const allNumeric = numbers.every((n: number) => Number.isFinite(n));
  if (!allNumeric) {
    return stringCol;
  }
  return numbers.every(isEpochMsLike) ? timestampCol : numberCol;
};

/**
 * Mint-time resolution of a per-cell datapoints dataset's inner `value`
 * `ColumnType` — the fetch-side rungs of the typing ladder, applied ONLY
 * when the declared type is `unknown` (a discriminator-less `TimeSeries`
 * ref the schema side could not type up front). A view that declared a
 * concrete string / number / timestamp value type wins outright — the
 * schema-side rungs (explicit valueType, CDM discriminator) take
 * precedence and pass through unchanged.
 *
 *   - the fetched series' authoritative `isString` flag is `true`
 *     ⇒ `string` (a string series must never be minted numeric and
 *     coerced to `NaN`);
 *   - otherwise the local {@link inferScalarType} reads the materialized
 *     datapoint values: an all-epoch-ms column ⇒ `timestamp` (instants
 *     render as dates), a numeric column ⇒ `number`, anything else (incl.
 *     no datapoints) ⇒ `string`.
 */
const resolveInnerValueCol = (declared: ColumnType, columns: Columns | undefined): ColumnType => {
  if (declared.kind !== 'unknown') {
    return declared;
  }
  if (columns?.isString?.[0] === true) {
    return stringCol;
  }
  return inferScalarType(columns?.value ?? []);
};

/**
 * Resolve a `ColumnType` to one of the value-axis kinds the provider
 * mints. A view passes the concrete column type; the helper keeps the
 * inner schema constructor honest by reusing the shared singletons.
 */
const valueColForType = (valueType: ColumnType | undefined): ColumnType => valueType ?? unknownCol;

export interface UseTimeseriesProviderOptions {
  readonly fetchDatapoints: DatapointsFetch;
  readonly defaultWindow: readonly [string, string];
  readonly id?: string;
}

/**
 * Build a timeseries provider over an injected datapoints fetch. `id` is
 * the dispatch + cache discriminator (`ProviderSource.providerId`);
 * `defaultWindow` is the fixed `[startISO, endISO]` resolved ONCE here
 * (never the wall clock at fetch time) so a source's cache key is stable
 * across turns. The provider serves the `timeseries` group; it claims
 * the single dataset name `datapoints` (the top-level source op), and is
 * also called directly by view providers via `buildSource` to mint
 * per-row cells.
 */
export const createTimeseriesProvider = ({
  id = TIMESERIES_PROVIDER_ID,
  fetchDatapoints,
  defaultWindow,
}: UseTimeseriesProviderOptions): DatasetProvider<TimeseriesParams, TimeseriesNative> => {
  // SSOT single-series cell minting — the function both a top-level
  // `(datapoints)` row and a view's per-row timeseries cell call, so the
  // two paths can never drift in inner shape.
  const buildSource: DatasetProvider<TimeseriesParams, TimeseriesNative>['buildSource'] = ({ ref, valueType }) => {
    const valueCol = valueColForType(valueType);
    const unit = unitOfType(valueCol);
    const native: TimeseriesSeriesNative = {
      kind: 'series',
      ref,
      valueKind: valueCol.kind,
      ...(unit === undefined ? {} : { valueUnit: [...unit] }),
      window: defaultWindow,
    };
    const source: ProviderSource = { kind: 'provider', providerId: id, native };
    return { source, schema: tsRefInnerSchema(valueCol) };
  };

  return {
    id,
    group: TIMESERIES_GROUP,
    claims: (name) => name === 'datapoints',
    // The top-level `datapoints` dataset's declared schema is the
    // row-per-series outer shape (identity columns + a nested cell).
    schema: () => rowsOuterSchema(),
    buildSource,
    // Lazy absorption of a downstream per-cell `time-bucket`: the node
    // STAYS in `plan.ops` and replays (so the cell still buckets), but
    // the absorbed hint refines the fetch window / granularity. Re-keying
    // the source cache on the adapted native keeps distinct push-downs
    // cached distinctly; the plan-level cache key is unaffected because
    // the op is not removed (HINT-style absorption). Only a single-series
    // source absorbs a per-cell time-bucket — a top-level rows source's
    // cells absorb their own.
    adaptPlan: (plan, source) => {
      const native = source.native as TimeseriesNative | undefined;
      if (native === undefined || native.kind !== 'series') {
        return source;
      }
      const hint = absorbTimeBucketHint(plan.ops);
      if (hint === undefined) {
        return source;
      }
      return { ...source, native: { ...native, bucket: hint } satisfies TimeseriesSeriesNative };
    },
    fetch: async (source, ctx) => {
      const native = source.native as TimeseriesNative | undefined;
      if (native === undefined) {
        throw new Error(`timeseries provider '${id}' fetch received a source with no native blob`);
      }
      if (native.kind === 'rows') {
        return buildDatapointsRows(buildSource, native.refs);
      }
      // MINT: box each fetched numeric value with the resolved unit (the
      // per-row stored unit a view resolves via `getUnit`, carried on the
      // native as JSON-stable entries). Units are value-level truth — the
      // materialized inner schema's column unit is then DERIVED from the
      // boxed cells via the shared refinement inference, never re-stamped
      // from the declared type.
      const unit = native.valueUnit === undefined ? undefined : new Map(native.valueUnit);
      const fetched = await fetchDatapoints(native, ctx);
      const columns =
        fetched?.value === undefined || unit === undefined
          ? fetched
          : { ...fetched, value: fetched.value.map((v) => boxNumericCell(v, unit)) };
      const resolved = resolveInnerValueCol(KIND_TO_COL[native.valueKind] ?? unknownCol, columns);
      const inner = tsRefInnerSchema(withUnit(resolved, unitOfType(inferColumnTypeFromCells(columns?.value ?? []))));
      // Inner per-cell timeseries dataset: not instance-backed, so its
      // identity axis is `undefined`.
      return new MaterializedDataset(columns ?? emptyInnerColumns(), inner, undefined);
    },
  };
};

/**
 * Reconstruct the inner `value` `ColumnType` from the JSON-stable
 * `valueKind` recorded on `native`. Only the scalar kinds the provider
 * mints (number / string / temporal / unknown) are possible; anything
 * else falls back to `unknown` so a stale/foreign native never crashes
 * the fetch. A declared temporal kind (a `valueType:'date'` measure)
 * must round-trip intact so an epoch-ms series renders as a date.
 */
const KIND_TO_COL: Partial<Record<ColumnType['kind'], ColumnType>> = {
  number: numberCol,
  string: stringCol,
  timestamp: timestampCol,
  date: dateCol,
  unknown: unknownCol,
};
